// src/components/appSettings/Contact/ContactInfoForm/contactFormHooks.js
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  createContactInfoThunk,
  updateContactInfoThunk,
} from "../../../../store/thunks/appSettingsThunks";

// Default form values - Aligned with backend schema
const getDefaultContactForm = (selectedBranch = "") => ({
  branchCode: selectedBranch,
  storeName: "",
  storeDescription: "",
  isMainBranch: false,
  establishedYear: new Date().getFullYear(),

  contact: {
    primaryPhone: "",
    secondaryPhone: "",
    whatsapp: "",
    email: "",
    website: "",
  },

  address: {
    line1: "",
    line2: "",
    landmark: "",
    city: "",
    state: "",
    country: "India",
    pincode: "",
    coordinates: {
      latitude: null,
      longitude: null,
    },
    googleMapsUrl: "",
  },

  businessHours: {
    regular: [
      {
        day: "MON",
        isOpen: true,
        openTime: "10:00",
        closeTime: "20:00",
        breakTime: { start: "", end: "" },
      },
      {
        day: "TUE",
        isOpen: true,
        openTime: "10:00",
        closeTime: "20:00",
        breakTime: { start: "", end: "" },
      },
      {
        day: "WED",
        isOpen: true,
        openTime: "10:00",
        closeTime: "20:00",
        breakTime: { start: "", end: "" },
      },
      {
        day: "THU",
        isOpen: true,
        openTime: "10:00",
        closeTime: "20:00",
        breakTime: { start: "", end: "" },
      },
      {
        day: "FRI",
        isOpen: true,
        openTime: "10:00",
        closeTime: "20:00",
        breakTime: { start: "", end: "" },
      },
      {
        day: "SAT",
        isOpen: true,
        openTime: "10:00",
        closeTime: "20:00",
        breakTime: { start: "", end: "" },
      },
      {
        day: "SUN",
        isOpen: true,
        openTime: "10:00",
        closeTime: "20:00",
        breakTime: { start: "", end: "" },
      },
    ],
    holidays: [],
  },

  socialMedia: {
    facebook: "",
    instagram: "",
    twitter: "",
    youtube: "",
    linkedin: "",
  },

  support: {
    customerCare: {
      phone: "",
      email: "",
      timings: "",
    },
    whatsappSupport: {
      enabled: true,
      number: "",
      businessAccount: false,
    },
    chatSupport: {
      enabled: true,
      platform: "",
      widgetId: "",
    },
  },

  services: {
    homeDelivery: false,
    videoShopping: false,
    appointmentBooking: true,
    customDesign: true,
    repair: true,
    exchange: true,
  },

  images: {
    storeFront: "",
    interior: [],
    logo: "",
  },

  certifications: [],

  isActive: true,
});

export const useContactForm = (initialData = null, mode = "add") => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { selectedBranch } = useSelector((state) => state.branch);

  const [formData, setFormData] = useState(() => {
    if (mode === "edit" && initialData) {
      return parseContactData(initialData);
    }
    return getDefaultContactForm(selectedBranch || "");
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Image files state (for new uploads)
  const [imageFiles, setImageFiles] = useState({
    storeFront: null,
    logo: null,
    interior: [], // Array of File objects
    certificationImages: [], // Array of File objects (sparse array matching certifications index)
  });

  // Preview URLs for images (can be blob URLs or existing server URLs)
  const [imagePreviews, setImagePreviews] = useState({
    storeFront: null,
    logo: null,
    interior: [], // Array of preview URLs
  });

  // Track which existing images to keep (for edit mode)
  const [existingImages, setExistingImages] = useState({
    storeFront: null,
    logo: null,
    interior: [], // Array of existing URLs
  });

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      // Cleanup all blob URLs to prevent memory leaks
      Object.values(imagePreviews).forEach((preview) => {
        if (Array.isArray(preview)) {
          preview.forEach((url) => {
            if (url?.startsWith("blob:")) {
              URL.revokeObjectURL(url);
            }
          });
        } else if (preview?.startsWith("blob:")) {
          URL.revokeObjectURL(preview);
        }
      });
    };
  }, []);

  // Update form when initial data changes (edit mode)
  useEffect(() => {
    if (mode === "edit" && initialData) {
      const parsed = parseContactData(initialData);
      setFormData(parsed);

      // Set existing image URLs
      const existing = {
        storeFront: parsed.images?.storeFront || null,
        logo: parsed.images?.logo || null,
        interior: parsed.images?.interior || [],
      };
      setExistingImages(existing);
      setImagePreviews(existing);
    }
  }, [initialData, mode]);

  // Update branchCode when selectedBranch changes (only for new contacts)
  useEffect(() => {
    if (mode === "add" && selectedBranch && !formData.branchCode) {
      setFormData((prev) => ({
        ...prev,
        branchCode: selectedBranch,
      }));
    }
  }, [selectedBranch, mode, formData.branchCode]);

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.includes(".")) {
      const parts = name.split(".");
      setFormData((prev) => {
        const newData = { ...prev };
        let current = newData;
        for (let i = 0; i < parts.length - 1; i++) {
          current[parts[i]] = { ...current[parts[i]] };
          current = current[parts[i]];
        }
        current[parts[parts.length - 1]] =
          type === "checkbox" ? checked : value;
        return newData;
      });
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Handle array changes
  const handleArrayChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle business hours change
  const handleBusinessHoursChange = (dayIndex, field, value) => {
    setFormData((prev) => ({
      ...prev,
      businessHours: {
        ...prev.businessHours,
        regular: prev.businessHours.regular.map((hour, index) =>
          index === dayIndex ? { ...hour, [field]: value } : hour
        ),
      },
    }));
  };

  // Handle break time changes
  const handleBreakTimeChange = (dayIndex, field, value) => {
    setFormData((prev) => ({
      ...prev,
      businessHours: {
        ...prev.businessHours,
        regular: prev.businessHours.regular.map((hour, index) =>
          index === dayIndex
            ? { ...hour, breakTime: { ...hour.breakTime, [field]: value } }
            : hour
        ),
      },
    }));
  };

  // Handle image file selection
  const handleImageChange = (e, imageType) => {
    const files = e.target.files;

    if (!files || files.length === 0) return;

    if (imageType === "interior") {
      const MAX_INTERIOR_IMAGES = 4;
      const currentCount = imagePreviews.interior.length;
      const filesArray = Array.from(files);

      // Check if adding new images would exceed the limit
      const availableSlots = MAX_INTERIOR_IMAGES - currentCount;

      if (availableSlots <= 0) {
        toast.error(`Maximum ${MAX_INTERIOR_IMAGES} interior images allowed`);
        e.target.value = ""; // Reset input
        return;
      }

      // Take only as many files as we have slots for
      const filesToAdd = filesArray.slice(0, availableSlots);

      if (filesToAdd.length < filesArray.length) {
        toast.warning(
          `Only ${availableSlots} more image(s) can be added. Maximum is ${MAX_INTERIOR_IMAGES} images.`
        );
      }

      // Create new file entries
      setImageFiles((prev) => ({
        ...prev,
        interior: [...prev.interior, ...filesToAdd],
      }));

      // Create preview URLs for new files
      const newPreviewUrls = filesToAdd.map((file) =>
        URL.createObjectURL(file)
      );

      setImagePreviews((prev) => ({
        ...prev,
        interior: [...prev.interior, ...newPreviewUrls],
      }));
    } else {
      // Handle single image (storeFront or logo)
      const file = files[0];

      // Revoke old blob URL if exists
      if (imagePreviews[imageType]?.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreviews[imageType]);
      }

      setImageFiles((prev) => ({
        ...prev,
        [imageType]: file,
      }));

      const previewUrl = URL.createObjectURL(file);
      setImagePreviews((prev) => ({
        ...prev,
        [imageType]: previewUrl,
      }));

      // Clear existing image since we're uploading a new one
      setExistingImages((prev) => ({
        ...prev,
        [imageType]: null,
      }));
    }

    // Reset the input value to allow re-selecting the same file
    e.target.value = "";
  };

  // Remove single image (storeFront or logo)
  const removeImage = (imageType) => {
    // Revoke blob URL if it exists
    if (imagePreviews[imageType]?.startsWith("blob:")) {
      URL.revokeObjectURL(imagePreviews[imageType]);
    }

    setImageFiles((prev) => ({
      ...prev,
      [imageType]: null,
    }));

    setImagePreviews((prev) => ({
      ...prev,
      [imageType]: null,
    }));

    setExistingImages((prev) => ({
      ...prev,
      [imageType]: null,
    }));
  };

  // Remove interior image by index
  const removeInteriorImage = (index) => {
    // Revoke blob URL if it exists
    if (imagePreviews.interior[index]?.startsWith("blob:")) {
      URL.revokeObjectURL(imagePreviews.interior[index]);
    }

    setImageFiles((prev) => ({
      ...prev,
      interior: prev.interior.filter((_, i) => i !== index),
    }));

    setImagePreviews((prev) => ({
      ...prev,
      interior: prev.interior.filter((_, i) => i !== index),
    }));

    setExistingImages((prev) => ({
      ...prev,
      interior: prev.interior.filter((_, i) => i !== index),
    }));
  };

  // Handle certification changes
  const handleCertificationChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      certifications: prev.certifications.map((cert, i) =>
        i === index ? { ...cert, [field]: value } : cert
      ),
    }));
  };

  // Add certification
  const addCertification = () => {
    setFormData((prev) => ({
      ...prev,
      certifications: [
        ...prev.certifications,
        {
          name: "",
          authority: "",
          number: "",
          validUntil: "",
          image: "",
        },
      ],
    }));
  };

  // Remove certification
  const removeCertification = (index) => {
    setFormData((prev) => ({
      ...prev,
      certifications: prev.certifications.filter((_, i) => i !== index),
    }));

    // Remove corresponding certification image
    setImageFiles((prev) => {
      const newCertImages = [...prev.certificationImages];
      newCertImages[index] = null;
      return {
        ...prev,
        certificationImages: newCertImages,
      };
    });
  };

  // Handle certification image upload
  const handleCertificationImageChange = (e, certIndex) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImageFiles((prev) => {
      const newCertImages = [...prev.certificationImages];
      newCertImages[certIndex] = file;
      return {
        ...prev,
        certificationImages: newCertImages,
      };
    });

    // Update the certification with a placeholder to indicate file is selected
    setFormData((prev) => ({
      ...prev,
      certifications: prev.certifications.map((cert, i) =>
        i === certIndex ? { ...cert, image: "pending_upload" } : cert
      ),
    }));
  };

  // Remove certification image
  const removeCertificationImage = (certIndex) => {
    setImageFiles((prev) => {
      const newCertImages = [...prev.certificationImages];
      newCertImages[certIndex] = null;
      return {
        ...prev,
        certificationImages: newCertImages,
      };
    });

    setFormData((prev) => ({
      ...prev,
      certifications: prev.certifications.map((cert, i) =>
        i === certIndex ? { ...cert, image: "" } : cert
      ),
    }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.branchCode || formData.branchCode.trim().length < 2) {
      newErrors.branchCode = "Branch code is required (min 2 characters)";
    }

    if (!formData.storeName || formData.storeName.trim().length < 3) {
      newErrors.storeName = "Store name is required (min 3 characters)";
    }

    if (!formData.contact.primaryPhone) {
      newErrors["contact.primaryPhone"] = "Primary phone is required";
    }

    if (!formData.contact.email) {
      newErrors["contact.email"] = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact.email)) {
      newErrors["contact.email"] = "Invalid email format";
    }

    if (!formData.address.line1) {
      newErrors["address.line1"] = "Address line 1 is required";
    }

    if (!formData.address.city) {
      newErrors["address.city"] = "City is required";
    }

    if (!formData.address.state) {
      newErrors["address.state"] = "State is required";
    }

    if (!formData.address.pincode) {
      newErrors["address.pincode"] = "Pincode is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error("Please fix the validation errors");
      return;
    }

    setSubmitting(true);

    try {
      const formDataToSend = new FormData();

      // Prepare clean contact data
      const submitData = {
        branchCode: formData.branchCode,
        storeName: formData.storeName,
        storeDescription: formData.storeDescription || undefined,
        establishedYear: formData.establishedYear,
        isMainBranch: formData.isMainBranch,
        isActive: formData.isActive,
        contact: {
          primaryPhone: formData.contact.primaryPhone,
          secondaryPhone: formData.contact.secondaryPhone || undefined,
          email: formData.contact.email,
          whatsapp: formData.contact.whatsapp || undefined,
          website: formData.contact.website || undefined,
        },
        address: {
          line1: formData.address.line1,
          line2: formData.address.line2 || undefined,
          landmark: formData.address.landmark || undefined,
          city: formData.address.city,
          state: formData.address.state,
          country: formData.address.country || "India",
          pincode: formData.address.pincode,
          coordinates: {
            latitude: formData.address.coordinates?.latitude || null,
            longitude: formData.address.coordinates?.longitude || null,
          },
          googleMapsUrl: formData.address.googleMapsUrl || undefined,
        },
        businessHours: {
          regular: formData.businessHours.regular.map((hour) => ({
            day: hour.day,
            isOpen: hour.isOpen,
            openTime: hour.openTime || undefined,
            closeTime: hour.closeTime || undefined,
            breakTime:
              hour.breakTime?.start && hour.breakTime?.end
                ? hour.breakTime
                : undefined,
          })),
          holidays: formData.businessHours.holidays || [],
        },
        socialMedia: formData.socialMedia,
        support: formData.support,
        services: formData.services,
        certifications: formData.certifications || [],
      };

      // In edit mode, preserve existing images that weren't changed
      if (mode === "edit") {
        submitData.existingImages = {
          storeFront: existingImages.storeFront || undefined,
          logo: existingImages.logo || undefined,
          interior: existingImages.interior || [],
        };
      }

      // Append JSON data as string
      formDataToSend.append("data", JSON.stringify(submitData));

      // Append new image files
      if (imageFiles.storeFront) {
        formDataToSend.append("storeFront", imageFiles.storeFront);
      }

      if (imageFiles.logo) {
        formDataToSend.append("logo", imageFiles.logo);
      }

      if (imageFiles.interior && imageFiles.interior.length > 0) {
        imageFiles.interior.forEach((file) => {
          formDataToSend.append("interior", file);
        });
      }

      if (
        imageFiles.certificationImages &&
        imageFiles.certificationImages.length > 0
      ) {
        imageFiles.certificationImages.forEach((file, index) => {
          if (file) {
            formDataToSend.append(`certificationImages`, file);
          }
        });
      }

      // Submit with FormData
      if (mode === "add") {
        await dispatch(createContactInfoThunk(formDataToSend)).unwrap();
        toast.success("Contact information created successfully");
      } else {
        await dispatch(
          updateContactInfoThunk({
            id: initialData._id,
            contactData: formDataToSend,
          })
        ).unwrap();
        toast.success("Contact information updated successfully");
      }

      navigate("/app-settings/contact");
    } catch (error) {
      console.error("Submit error:", error);
      toast.error(error.message || `Failed to ${mode} contact information`);
    } finally {
      setSubmitting(false);
    }
  };

  return {
    formData,
    setFormData,
    errors,
    setErrors,
    submitting,
    imageFiles,
    imagePreviews,
    existingImages,
    handleChange,
    handleArrayChange,
    handleBusinessHoursChange,
    handleBreakTimeChange,
    handleImageChange,
    removeImage,
    removeInteriorImage,
    handleCertificationChange,
    addCertification,
    removeCertification,
    handleCertificationImageChange,
    removeCertificationImage,
    handleSubmit,
    validateForm,
  };
};

// Helper function to parse contact data for edit mode
function parseContactData(contact) {
  return {
    branchCode: contact.branchCode || "",
    storeName: contact.storeName || "",
    storeDescription: contact.storeDescription || "",
    isMainBranch: contact.isMainBranch || false,
    establishedYear: contact.establishedYear || new Date().getFullYear(),

    contact: {
      primaryPhone: contact.contact?.primaryPhone || "",
      secondaryPhone: contact.contact?.secondaryPhone || "",
      email: contact.contact?.email || "",
      whatsapp: contact.contact?.whatsapp || "",
      website: contact.contact?.website || "",
    },

    address: {
      line1: contact.address?.line1 || "",
      line2: contact.address?.line2 || "",
      landmark: contact.address?.landmark || "",
      city: contact.address?.city || "",
      state: contact.address?.state || "",
      country: contact.address?.country || "India",
      pincode: contact.address?.pincode || "",
      coordinates: {
        latitude: contact.address?.coordinates?.latitude || null,
        longitude: contact.address?.coordinates?.longitude || null,
      },
      googleMapsUrl: contact.address?.googleMapsUrl || "",
    },

    businessHours:
      contact.businessHours || getDefaultContactForm().businessHours,

    socialMedia: {
      facebook: contact.socialMedia?.facebook || "",
      instagram: contact.socialMedia?.instagram || "",
      twitter: contact.socialMedia?.twitter || "",
      youtube: contact.socialMedia?.youtube || "",
      linkedin: contact.socialMedia?.linkedin || "",
    },

    support: {
      customerCare: {
        phone: contact.support?.customerCare?.phone || "",
        email: contact.support?.customerCare?.email || "",
        timings: contact.support?.customerCare?.timings || "",
      },
      whatsappSupport: {
        enabled:
          contact.support?.whatsappSupport?.enabled !== undefined
            ? contact.support.whatsappSupport.enabled
            : true,
        number: contact.support?.whatsappSupport?.number || "",
        businessAccount:
          contact.support?.whatsappSupport?.businessAccount || false,
      },
      chatSupport: {
        enabled:
          contact.support?.chatSupport?.enabled !== undefined
            ? contact.support.chatSupport.enabled
            : true,
        platform: contact.support?.chatSupport?.platform || "",
        widgetId: contact.support?.chatSupport?.widgetId || "",
      },
    },

    services: {
      homeDelivery: contact.services?.homeDelivery || false,
      videoShopping: contact.services?.videoShopping || false,
      appointmentBooking:
        contact.services?.appointmentBooking !== undefined
          ? contact.services.appointmentBooking
          : true,
      customDesign:
        contact.services?.customDesign !== undefined
          ? contact.services.customDesign
          : true,
      repair:
        contact.services?.repair !== undefined ? contact.services.repair : true,
      exchange:
        contact.services?.exchange !== undefined
          ? contact.services.exchange
          : true,
    },

    images: {
      storeFront: contact.images?.storeFront || "",
      interior: contact.images?.interior || [],
      logo: contact.images?.logo || "",
    },

    certifications: contact.certifications || [],

    isActive: contact.isActive !== undefined ? contact.isActive : true,
  };
}
